import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import {
  Box,
  Container,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Paper
} from '@material-ui/core';
import { Copy as CopyIcon, RefreshCw as RefreshIcon } from 'react-feather';
import affiliateService from '../../services/affiliateService';

const LinkAdd = () => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [generatedLink, setGeneratedLink] = useState(null);

  const [formData, setFormData] = useState({
    channel_id: '',
    target_url: 'https://www.divacosmeticos.com.br',
    custom_code: ''
  });

  const loadChannels = async () => {
    try {
      const response = await affiliateService.getChannels(auth.token, { approved: true });

      if (response && response.data) {
        setChannels(response.data.data || []);

        // Se tiver apenas um canal aprovado, seleciona automaticamente
        if (response.data.data && response.data.data.length === 1) {
          setFormData((prev) => ({
            ...prev,
            channel_id: response.data.data[0].id
          }));
        }
      }
    } catch (error) {
      console.error(error);
      swal('Erro', 'Erro ao carregar canais', 'error');
    } finally {
      setLoadingChannels(false);
    }
  };

  useEffect(() => {
    loadChannels();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({
      ...formData,
      custom_code: code
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneratedLink(null);

    try {
      const response = await affiliateService.createAffiliateLink(formData, auth.token);

      if (response && response.data) {
        setGeneratedLink(response.data);
        swal('Sucesso!', 'Link de afiliado criado com sucesso!', 'success');

        // Limpar formulário
        setFormData({
          channel_id: formData.channel_id, // Mantém o canal selecionado
          target_url: 'https://www.divacosmeticos.com.br',
          custom_code: ''
        });
      } else {
        throw new Error('Erro ao criar link');
      }
    } catch (error) {
      console.error(error);

      // Tratamento específico para código duplicado
      if (error.response?.data?.error?.includes('já está em uso')) {
        swal('Código em uso', 'Este código já está sendo usado. Escolha outro código.', 'warning');
      } else {
        swal('Erro', 'Erro ao criar link de afiliado', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    swal('Copiado!', 'Link copiado para área de transferência', 'success');
  };

  if (loadingChannels) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (channels.length === 0) {
    return (
      <>
        <Helmet>
          <title>Gerar Link | Sistema de Afiliados</title>
        </Helmet>
        <Box
          sx={{
            backgroundColor: 'background.default',
            minHeight: '100%',
            py: 3
          }}
        >
          <Container maxWidth="lg">
            <Alert severity="warning">
              <Typography variant="h6">Nenhum canal aprovado</Typography>
              <Typography>
                Você precisa ter pelo menos um canal aprovado para gerar links de afiliado.
                Cadastre um canal e aguarde a aprovação do administrador.
              </Typography>
              <Button
                color="primary"
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => navigate('/app/afiliado')}
              >
                Cadastrar Canal
              </Button>
            </Alert>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gerar Novo Link | Sistema de Afiliados</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <form onSubmit={handleSubmit}>
                <Card>
                  <CardHeader
                    title="Gerador de Links de Afiliado"
                    subheader="Crie links personalizados com rastreamento UTM"
                  />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined" required>
                          <InputLabel>Selecione o Canal</InputLabel>
                          <Select
                            label="Selecione o Canal"
                            name="channel_id"
                            value={formData.channel_id}
                            onChange={handleChange}
                          >
                            {channels.map((channel) => (
                              <MenuItem key={channel.id} value={channel.id}>
                                {channel.name} ({channel.platform})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Código do Link (Opcional)"
                          name="custom_code"
                          value={formData.custom_code}
                          onChange={handleChange}
                          variant="outlined"
                          helperText="Deixe vazio para gerar automaticamente"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={generateRandomCode} edge="end">
                                  <RefreshIcon size={18} />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="URL de Destino"
                          name="target_url"
                          required
                          value={formData.target_url}
                          onChange={handleChange}
                          variant="outlined"
                          helperText="URL da loja para onde o link vai redirecionar"
                          type="url"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      p: 2,
                      gap: 2
                    }}
                  >
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={() => navigate('/app/links')}
                      disabled={loading}
                    >
                      Voltar
                    </Button>
                    <Button color="primary" variant="contained" type="submit" disabled={loading}>
                      {loading ? 'Gerando...' : 'Gerar Link'}
                    </Button>
                  </Box>
                </Card>
              </form>
            </Grid>

            {generatedLink && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: 'success.light' }}>
                  <Typography variant="h6" gutterBottom color="success.dark">
                    ✅ Link Gerado com Sucesso!
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Código:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {generatedLink.code}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(generatedLink.code)}
                      >
                        <CopyIcon size={18} />
                      </IconButton>
                    </Box>

                    <Typography variant="subtitle2" color="textSecondary">
                      Link Completo com UTM:
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        bgcolor: 'white',
                        borderRadius: 1,
                        wordBreak: 'break-all'
                      }}
                    >
                      <Typography variant="body2">{generatedLink.full_link}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(generatedLink.full_link)}
                      >
                        <CopyIcon size={18} />
                      </IconButton>
                    </Box>

                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mt: 2 }}
                      onClick={() => navigate('/app/links')}
                    >
                      Ver Meus Links
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default LinkAdd;
